/**
 * Newsletter.tsx — 공개 뉴스레터 소개 + 구독 가입 (Double opt-in)
 * SEO 최적화 · 12개 섹션 소개 · 실제 메일/사이트 스크린샷 · 가입 폼
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import DayBriefCard from '../daily-brief/DayBriefCard'
import type { DayBriefBundle } from '../../lib/dailyBriefApi'
import {
  FiMail, FiPlay, FiClock, FiShield, FiCheckCircle, FiAlertOctagon,
  FiBarChart2, FiBookOpen, FiBell, FiFileText, FiCheckSquare,
  FiHelpCircle, FiAward, FiUsers, FiCalendar, FiYoutube, FiMic,
  FiArrowRight, FiLock,
} from 'react-icons/fi'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import Button from '../../components/ui/Button'
import Brand, { renderWithBrand } from '../../components/brand/Brand'
import { cn } from '../../lib/utils'

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.55 },
}

interface SubscribeResp {
  ok: boolean
  status: string
  message: string
}

export default function Newsletter() {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [agree, setAgree] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)

  // 해시(#today, #subscribe 등)로 진입 시 해당 섹션으로 부드럽게 스크롤
  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (!hash) return
    // PublicLatestBrief 등 동적 콘텐츠가 마운트된 후 스크롤되도록 약간 대기
    const id = window.setTimeout(() => {
      const el = document.getElementById(hash)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 300)
    return () => window.clearTimeout(id)
  }, [])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!agree) {
      setResult({ ok: false, message: '광고성 정보 수신에 동의해 주세요.' })
      return
    }
    setSubmitting(true)
    setResult(null)
    try {
      const r = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          display_name: name.trim() || null,
          locale: 'ko',
          marketing_consent: true,
          source: 'landing',
        }),
      })
      const data = (await r.json()) as SubscribeResp | { detail: string }
      if (r.ok && 'ok' in data && data.ok) {
        setResult({ ok: true, message: data.message })
        if (data.status !== 'already_active') {
          setEmail(''); setName(''); setAgree(false)
        }
      } else {
        const msg = 'detail' in data ? data.detail : '구독 신청 중 오류가 발생했습니다.'
        setResult({ ok: false, message: msg })
      }
    } catch {
      setResult({ ok: false, message: '네트워크 오류 — 잠시 후 다시 시도해 주세요.' })
    } finally {
      setSubmitting(false)
    }
  }

  // 폼 JSX — 히어로(compact) + 하단(풀) 두 곳에서 재사용
  const subscribeForm = (variant: 'compact' | 'full') => {
    const compact = variant === 'compact'
    return (
      <form onSubmit={handleSubmit}
        className={cn(
          'space-y-4',
          compact
            ? 'bg-zinc-900/70 backdrop-blur border border-white/10 rounded-2xl p-6 shadow-2xl'
            : 'bg-zinc-900/60 backdrop-blur border border-white/10 rounded-2xl p-7 sm:p-9 shadow-xl space-y-5',
        )}>
        {compact && (
          <div className="text-center pb-1">
            <div className="text-[10px] tracking-[0.3em] text-amber-300 font-bold uppercase mb-1">FREE · NO CREDIT CARD</div>
            <div className="text-base font-bold text-white">지금 무료로 구독 신청</div>
          </div>
        )}
        <div>
          <label htmlFor={compact ? 'nl-email-c' : 'nl-email'} className="block text-xs font-bold text-zinc-300 uppercase tracking-wider mb-2">
            이메일 <span className="text-red-400">*</span>
          </label>
          <input
            id={compact ? 'nl-email-c' : 'nl-email'} type="email" required value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={submitting}
            className="w-full px-4 py-3 bg-zinc-950/60 border border-white/10 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition disabled:opacity-60"
          />
        </div>
        <div>
          <label htmlFor={compact ? 'nl-name-c' : 'nl-name'} className="block text-xs font-bold text-zinc-300 uppercase tracking-wider mb-2">
            이름 또는 직함 <span className="text-zinc-500 font-normal normal-case">(선택)</span>
          </label>
          <input
            id={compact ? 'nl-name-c' : 'nl-name'} type="text" maxLength={60} value={name}
            onChange={e => setName(e.target.value)}
            placeholder={compact ? '예: 홍길동' : '예: 홍길동 안전관리자'}
            disabled={submitting}
            className="w-full px-4 py-3 bg-zinc-950/60 border border-white/10 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition disabled:opacity-60"
          />
        </div>
        <label className={cn(
          'flex items-start gap-3 cursor-pointer select-none rounded-lg bg-zinc-950/40 border border-white/10 hover:border-amber-400/40 transition',
          compact ? 'p-3' : 'p-4',
        )}>
          <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
            className="mt-1 w-4 h-4 accent-amber-400 flex-none" />
          <span className={cn('text-zinc-300 leading-relaxed', compact ? 'text-[11px]' : 'text-xs')}>
            <strong className="text-white block mb-0.5">광고성 정보 수신 동의 (필수)</strong>
            {compact
              ? '매일 아침 07시 발송에 동의합니다. 1-click 해지 가능.'
              : <>매일 아침 07시 <Brand /> 산업안전 뉴스레터 발송에 동의합니다. 언제든 메일 하단 1-click 해지 링크로 수신 거부할 수 있습니다. 자세한 사항은 <Link to="/privacy" className="text-amber-300 underline">개인정보 처리방침</Link>을 참고해 주세요.</>
            }
          </span>
        </label>
        <Button type="submit" disabled={submitting || !email || !agree}
          variant="primary" size={compact ? 'md' : 'lg'} className="w-full justify-center">
          {submitting ? '신청 중…' : (
            <span className="inline-flex items-center gap-2">
              <FiMail size={compact ? 16 : 18} /> 무료 구독 신청
            </span>
          )}
        </Button>
        {result && (
          <div className={cn(
            'rounded-lg p-3 border',
            compact ? 'text-xs' : 'text-sm p-4',
            result.ok
              ? 'bg-emerald-500/[0.08] border-emerald-500/30 text-emerald-200'
              : 'bg-red-500/[0.08] border-red-500/30 text-red-200',
          )}>
            {result.message}
          </div>
        )}
        {!compact && (
          <p className="text-[11px] text-zinc-500 leading-relaxed text-center">
            가입 즉시 인증 메일이 발송됩니다. 24시간 이내에 메일 안의 인증 링크를 눌러주세요.
          </p>
        )}
      </form>
    )
  }

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <title>Heinrich Safe Brief — AI 산업안전 일일 뉴스레터 무료 구독</title>
        <meta
          name="description"
          content="매일 아침 07시, 행정안전부 일일상황·KOSHA 가이드·산업안전 뉴스·법령 변경·판례를 AI가 한 편으로 정리해 보내드립니다. 4컷 사고 사례·체크리스트·퀴즈·팟캐스트·YouTube 영상까지 모두 무료. Heinrich 서비스 가입 없이도 이메일만으로 구독 가능합니다."
        />
        <meta
          name="keywords"
          content="산업안전 뉴스레터, 안전관리자 뉴스레터, KOSHA, 중대재해처벌법, 산업재해예방, 안전보건, 안전교육, AI 산업안전, 무료 뉴스레터, 일일 안전 브리핑, Heinrich, 하인리히"
        />
        <link rel="canonical" href="https://heinrich.dasancms.co.kr/newsletter/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Heinrich Safe Brief — AI 산업안전 일일 뉴스레터" />
        <meta property="og:description" content="매일 07시 발송. 사고 사례·통계·법령·판례·KOSHA·체크리스트·퀴즈·팟캐스트·영상. 무료 구독." />
        <meta property="og:image" content="https://heinrich.dasancms.co.kr/newsletter-assets/hero-mail.png" />
        <meta property="og:url" content="https://heinrich.dasancms.co.kr/newsletter/" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: 'Heinrich Safe Brief — AI 산업안전 일일 뉴스레터',
          publisher: {
            '@type': 'Organization', name: 'Dasan AI',
            logo: { '@type': 'ImageObject', url: 'https://heinrich.dasancms.co.kr/main-logo.png' },
          },
          description: '매일 아침 07시 발송되는 AI 기반 산업안전 일일 브리핑 뉴스레터',
          inLanguage: 'ko-KR',
          isAccessibleForFree: true,
        })}</script>
      </Helmet>

      {/* ───────────── 히어로 — Pricing/Landing 패턴 차용한 풍부한 백그라운드 ───────────── */}
      <section className="relative overflow-hidden bg-zinc-950 pt-32 pb-24 px-6 border-b border-white/[0.06]">
        {/* 다중 글로우 — 우상단 앰버, 좌하단 보라/오렌지 */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -right-32 w-[700px] h-[700px] rounded-full opacity-30 blur-[120px]"
          style={{ background: 'radial-gradient(circle, #fcd34d 0%, transparent 70%)' }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full opacity-20 blur-[100px]"
          style={{ background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)' }}
        />
        {/* 미세 그리드 패턴 — 산업 디자인 톤 */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeInUp}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold tracking-wider uppercase mb-6">
              <FiClock size={13} /> 매일 아침 07:00 발송
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6">
              어제의 산업 안전,<br />
              <span className="text-amber-300">오늘 아침 한 번에.</span>
            </h1>
            <p className="text-lg text-zinc-300 leading-relaxed mb-8 max-w-xl">
              행정안전부 일일상황보고, KOSHA 신규 가이드, 산업안전 뉴스, 법령·고시 변경, 어제의 판결까지 —
              매일 아침 AI가 한 편의 메일로 정리해 보내드립니다. <strong className="text-white"><Brand /> 가입 없이도 이메일만으로 무료 구독</strong> 가능합니다.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              {['4컷 사고 사례', '주간 통계', '주요 뉴스 5건', '체크리스트', '4지선다 퀴즈', '팟캐스트', 'YouTube 영상'].map(t => (
                <span key={t} className="text-xs font-semibold text-zinc-300 bg-white/[0.06] border border-white/10 px-3 py-1.5 rounded-full">
                  {t}
                </span>
              ))}
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              이메일만 입력하시면 됩니다 · 가입비·신용카드 X · 1-click 해지
            </p>
          </motion.div>
          <motion.div {...fadeInUp} className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-amber-500/20 via-transparent to-transparent rounded-3xl blur-2xl pointer-events-none" aria-hidden />
            <div className="relative">{subscribeForm('compact')}</div>
            <div className="text-center mt-4">
              <a href="#today" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-300 transition">
                실제 발송 메일·사이트 미리보기 보기 <FiArrowRight size={12} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────────── 핵심 가치 3 — Pricing 스타일 그라데이션 카드 ───────────── */}
      <section className="bg-zinc-950 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <div className="text-xs tracking-[0.3em] text-amber-300 font-bold uppercase mb-3">WHY HEINRICH</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">선택받는 세 가지 이유</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: FiClock, t: '07:00, 단 1통', d: '매일 정확히 한 편. 시간 낭비 없는 큐레이션.', g: 'from-blue-600/25 to-indigo-600/10', i: 'text-blue-300', b: 'border-blue-500/30' },
              { icon: FiShield, t: '공식 데이터 100%', d: '행정안전부·KOSHA·법제처·언론 보도 기반. 가짜 정보 없음.', g: 'from-emerald-600/25 to-teal-600/10', i: 'text-emerald-300', b: 'border-emerald-500/30' },
              { icon: FiAward, t: '현장 적용 중심', d: '읽고 끝나지 않는 체크리스트·퀴즈·Q&A로 학습 효과.', g: 'from-amber-600/25 to-orange-600/10', i: 'text-amber-300', b: 'border-amber-500/30' },
            ].map((b, i) => (
              <motion.div key={i} {...fadeInUp} transition={{ duration: 0.5, delay: i * 0.08 }}
                className={cn(
                  'relative rounded-2xl border p-7 bg-gradient-to-br shadow-lg shadow-black/20',
                  'hover:-translate-y-1 hover:shadow-amber-500/10 transition-all duration-300',
                  b.g, b.b,
                )}>
                <div className={cn('w-12 h-12 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-5', b.i)}>
                  <b.icon size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{b.t}</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">{b.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── 섹션 디바이더 ───────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden />

      {/* ───────────── 오늘의 뉴스레터 — 실제 데이터 노출 ───────────── */}
      <section id="today" className="relative bg-[#060a14] py-24 px-6 overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-15 blur-[120px]"
          style={{ background: 'radial-gradient(circle, #fcd34d 0%, transparent 70%)' }}
        />
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <div className="text-xs tracking-[0.3em] text-amber-300 font-bold uppercase mb-3">TODAY'S BRIEF</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              오늘의 뉴스레터, 미리 보기
            </h2>
            <p className="text-sm text-zinc-400">
              구독자가 받는 실제 뉴스레터의 모든 내용을 바로 살펴보세요.
            </p>
          </motion.div>
          <PublicLatestBrief />
        </div>
      </section>

      {/* ───────────── 섹션 디바이더 ───────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden />

      {/* ───────────── 12개 섹션 소개 — bento 그리드 풍 ───────────── */}
      <section className="relative bg-zinc-950 py-24 px-6 overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 right-1/3 w-[600px] h-[400px] rounded-full opacity-[0.08] blur-[120px]"
          style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 70%)' }}
        />
        <div className="relative max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <div className="text-xs tracking-[0.3em] text-amber-300 font-bold uppercase mb-3">IN EACH ISSUE</div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              한 통에 담긴, 12개의 섹션
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              아침 출근길 4~5분이면 산업 안전 하루의 핵심을 모두 파악할 수 있도록 구성했습니다.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { i: FiAlertOctagon, t: '오늘의 사고 사례', d: '행정안전부 일일상황 사고를 4컷 만화·원인·체크포인트로 시각화', col: 'red' },
              { i: FiBarChart2, t: '이번 주 사고 통계', d: '주간 사고·사망 집계와 유형·업종 Top 3 비교', col: 'purple' },
              { i: FiBookOpen, t: '어제의 판결', d: '전날 산업안전 관련 법원 판결(법원·사건번호 포함)', col: 'cyan' },
              { i: FiFileText, t: '법령·고시 변경', d: '법제처 nightly 모니터링으로 산업안전 관련 신규 시행', col: 'blue' },
              { i: FiShield, t: 'KOSHA 신규 가이드', d: '한국산업안전보건공단 신규 기술지침·매뉴얼 알림', col: 'emerald' },
              { i: FiBell, t: '주요 산업안전 뉴스', d: '주요 매체 산업안전 뉴스 다이제스트 5건, 5분할 헤더 일러스트', col: 'orange' },
              { i: FiCalendar, t: '이달의 안전 캠페인', d: '월별 계절성·법정 캠페인 안전 테마 + 현장 점검 포인트', col: 'amber' },
              { i: FiUsers, t: '이번 주 아차사고', d: '과거 30일 사고 1건을 익명·교훈 중심으로 학습 자료화', col: 'pink' },
              { i: FiCheckSquare, t: '오늘의 체크리스트', d: '오늘 현장 시작 전 점검할 5~7개 행동 항목', col: 'lime' },
              { i: FiHelpCircle, t: '안전관리자 Q&A', d: '안전관리자가 자주 묻는 질문 1건 + 명확한 답', col: 'sky' },
              { i: FiAward, t: '오늘의 안전 퀴즈', d: '4지선다 1문항 — 그 자리에서 정답·해설 공개', col: 'violet' },
              { i: FiMic, t: '2인 진행자 팟캐스트', d: '약 4~7분, 모든 섹션을 라디오 톤 대화로 풀어드림', col: 'indigo' },
            ].map((s, i) => {
              // 그라데이션 + 보더 + 아이콘 컬러 (zinc 베이스에 액센트만)
              const colorMap: Record<string, { g: string; b: string; i: string }> = {
                red:     { g: 'from-red-600/15 to-zinc-900',     b: 'border-red-500/25',     i: 'text-red-300' },
                purple:  { g: 'from-purple-600/15 to-zinc-900',  b: 'border-purple-500/25',  i: 'text-purple-300' },
                cyan:    { g: 'from-cyan-600/15 to-zinc-900',    b: 'border-cyan-500/25',    i: 'text-cyan-300' },
                blue:    { g: 'from-blue-600/15 to-zinc-900',    b: 'border-blue-500/25',    i: 'text-blue-300' },
                emerald: { g: 'from-emerald-600/15 to-zinc-900', b: 'border-emerald-500/25', i: 'text-emerald-300' },
                orange:  { g: 'from-orange-600/15 to-zinc-900',  b: 'border-orange-500/25',  i: 'text-orange-300' },
                amber:   { g: 'from-amber-600/15 to-zinc-900',   b: 'border-amber-500/25',   i: 'text-amber-300' },
                pink:    { g: 'from-pink-600/15 to-zinc-900',    b: 'border-pink-500/25',    i: 'text-pink-300' },
                lime:    { g: 'from-lime-600/15 to-zinc-900',    b: 'border-lime-500/25',    i: 'text-lime-300' },
                sky:     { g: 'from-sky-600/15 to-zinc-900',     b: 'border-sky-500/25',     i: 'text-sky-300' },
                violet:  { g: 'from-violet-600/15 to-zinc-900',  b: 'border-violet-500/25',  i: 'text-violet-300' },
                indigo:  { g: 'from-indigo-600/15 to-zinc-900',  b: 'border-indigo-500/25',  i: 'text-indigo-300' },
              }
              const c = colorMap[s.col]
              return (
                <motion.div key={s.t} {...fadeInUp} transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                  className={cn(
                    'group relative rounded-2xl border p-5 bg-gradient-to-br',
                    'hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-500/5 transition-all duration-300',
                    c.g, c.b,
                  )}>
                  <div className={cn('w-10 h-10 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center mb-3', c.i)}>
                    <s.i size={20} />
                  </div>
                  <div className="text-base font-bold text-white mb-1.5">{s.t}</div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{s.d}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ───────────── 섹션 디바이더 ───────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden />

      {/* ───────────── 사고 사례 클로즈업 (스크린샷) ───────────── */}
      <section className="bg-[#060a14] py-24 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeInUp}>
            <div className="text-xs tracking-[0.3em] text-red-400 font-bold uppercase mb-3">SECTION 01 · ACCIDENT</div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
              4컷 만화 + 안전 체크포인트로 <br />
              <span className="text-red-300">사고를 한눈에.</span>
            </h2>
            <p className="text-zinc-300 leading-relaxed mb-6">
              매일 행정안전부 일일상황 사업장사고 중 핵심 1건을 골라, AI가 4컷 예방 만화로 시각화합니다.
              사고 개요·발생일·직접 원인·현장 체크포인트가 한 카드에 모두 정리됩니다.
            </p>
            <ul className="space-y-3 text-sm text-zinc-300">
              {[
                '사고 유형·발생일·위치(마스킹) 자동 분류',
                '4컷 일러스트로 사고 흐름 즉시 이해',
                '안전 체크포인트 3개 + 직접 원인 분석',
                '회사명·실명·정확한 지역은 자동 마스킹',
              ].map(t => (
                <li key={t} className="flex items-start gap-2">
                  <FiCheckCircle className="text-red-400 mt-0.5 flex-none" size={16} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeInUp} className="relative">
            {/* 글로우 + 프레임 */}
            <div className="absolute -inset-6 bg-gradient-to-br from-red-500/15 via-transparent to-amber-500/10 rounded-3xl blur-2xl pointer-events-none" aria-hidden />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]">
              <img
                src="/newsletter-assets/sample-incident.png"
                alt="Heinrich 사고 사례 카드 — 4컷 만화와 안전 체크포인트"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────────── 섹션 디바이더 ───────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden />

      {/* ───────────── 사이트 미리보기 — 가입자 혜택 ───────────── */}
      <section className="relative bg-zinc-950 py-24 px-6 overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 left-0 w-[600px] h-[400px] rounded-full opacity-[0.10] blur-[120px]"
          style={{ background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)' }}
        />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeInUp} className="lg:order-2">
            <div className="text-xs tracking-[0.3em] text-cyan-400 font-bold uppercase mb-3">MEMBER ONLY</div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
              <Brand /> 가입자는 <br />
              <span className="text-cyan-300">과거 회차와 전체 내용까지.</span>
            </h2>
            <p className="text-zinc-300 leading-relaxed mb-6">
              비가입 구독자는 매일 도착하는 그날의 메일을 받지만,
              <strong className="text-white"> <Brand /> 서비스에 가입하시면 모든 과거 회차 아카이브, 사고·법령·판례 검색,
              관심 산업·사고 유형 맞춤 필터, AI 챗봇 학습 도구</strong>까지 추가로 사용하실 수 있습니다.
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {['모든 과거 회차 아카이브', '사고·법령·판례 검색', '관심 산업 맞춤 필터', 'AI 안전 챗봇', 'JSA·TBM·작업허가서 등'].map(t => (
                <span key={t} className="text-xs font-semibold text-cyan-200 bg-cyan-500/[0.08] border border-cyan-500/30 px-3 py-1.5 rounded-full">
                  {t}
                </span>
              ))}
            </div>
            <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-zinc-950 font-bold text-sm transition-colors shadow-lg shadow-cyan-500/20">
              <Brand /> 무료로 시작하기 <FiArrowRight />
            </Link>
          </motion.div>
          <motion.div {...fadeInUp} className="lg:order-1 relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-cyan-500/15 via-transparent to-blue-500/10 rounded-3xl blur-2xl pointer-events-none" aria-hidden />
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]">
              <img
                src="/newsletter-assets/sample-page.png"
                alt="Heinrich 사이트 daily-brief 페이지 미리보기"
                className="w-full h-auto block"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────────── 섹션 디바이더 ───────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden />

      {/* ───────────── 가입 폼 — 풀블리드 CTA ───────────── */}
      <section id="subscribe" className="relative bg-zinc-950 py-24 px-6 overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full opacity-25 blur-[120px]"
          style={{ background: 'radial-gradient(ellipse, #fcd34d 0%, transparent 70%)' }}
        />
        <div className="relative max-w-2xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold tracking-wider uppercase mb-5">
              <FiMail size={13} /> FREE · NO CREDIT CARD
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              지금 구독 신청하기
            </h2>
            <p className="text-zinc-400">
              이메일만 입력하시면 인증 메일을 보내드립니다. <br className="hidden sm:block" />
              인증을 완료한 다음 날 아침 07시부터 매일 한 편씩 도착합니다.
            </p>
          </motion.div>
          {subscribeForm('full')}
        </div>
      </section>

      {/* ───────────── 섹션 디바이더 ───────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" aria-hidden />

      {/* ───────────── FAQ ───────────── */}
      <section className="bg-[#060a14] py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-xs tracking-[0.3em] text-amber-300 font-bold uppercase mb-3">FAQ</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">자주 묻는 질문</h2>
          </div>
          <div className="space-y-5">
            {[
              { q: '구독료가 있나요?', a: '아니요, 완전 무료입니다. Heinrich 서비스에 가입하지 않은 분도 이메일만 입력하시면 매일 받아보실 수 있습니다.' },
              { q: 'Heinrich 서비스 가입과 뭐가 다른가요?', a: '뉴스레터는 매일 한 편의 메일만 받습니다. Heinrich 서비스에 가입하시면 과거 회차 다시 보기, AI 챗봇 학습 도구, JSA·TBM·작업허가서 등 모든 산업안전 도구를 사용할 수 있습니다.' },
              { q: '수신을 해지할 수 있나요?', a: '메일 하단의 1-click 해지 링크를 누르시면 즉시 처리됩니다. 별도 로그인 절차가 필요 없습니다.' },
              { q: '내 정보는 어떻게 보호되나요?', a: '이메일과 가입 시각, 동의 시각만 저장합니다. 광고 식별·외부 공유에는 사용하지 않으며, 해지 시 발송 대상에서 즉시 제외됩니다.' },
              { q: '발송 시간은 정확한가요?', a: '매일 07:00 KST 자동 발송됩니다. 행정안전부 보고서 수신 직후 AI가 콘텐츠를 생성·검증한 뒤 발송하는 구조입니다.' },
              { q: '인증 메일이 안 와요.', a: '스팸함을 확인하시거나 24시간이 지나면 다시 신청해 주세요. 그래도 문제가 있다면 heinrich_dasan@naver.com 으로 연락 주시면 도와드립니다.' },
            ].map(f => (
              <details key={f.q} className="group bg-zinc-950/40 border border-white/10 rounded-xl px-5 py-4 hover:border-amber-400/30 transition">
                <summary className="cursor-pointer text-white font-semibold flex items-center justify-between gap-3 list-none">
                  <span>{renderWithBrand(f.q)}</span>
                  <FiArrowRight className="text-zinc-500 group-open:rotate-90 transition-transform flex-none" size={16} />
                </summary>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{renderWithBrand(f.a)}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── 가입자 한정 기능 안내 — 풀블리드 그라데이션 ───────────── */}
      <section className="relative bg-zinc-950 py-24 px-6 overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 left-1/4 w-[600px] h-[600px] rounded-full opacity-25 blur-[120px]"
          style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 70%)' }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 right-1/4 w-[500px] h-[500px] rounded-full opacity-20 blur-[120px]"
          style={{ background: 'radial-gradient(circle, #fcd34d 0%, transparent 70%)' }}
        />
        <div className="relative max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-indigo-950/60 via-zinc-900/80 to-zinc-950/80 backdrop-blur border border-white/10 p-10 sm:p-14 shadow-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold tracking-wider uppercase mb-5">
            <FiLock size={13} /> <Brand /> 가입자 전용
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            더 깊게 학습하고 싶으시다면
          </h2>
          <p className="text-zinc-300 leading-relaxed mb-8 max-w-3xl">
            뉴스레터는 매일 한 통의 큐레이션을 제공합니다. <Brand /> 서비스 가입자는 추가로 아래 도구를 사용할 수 있습니다.
          </p>
          <div className="grid md:grid-cols-2 gap-3 mb-10">
            {[
              '과거 회차 다시 보기 + 검색',
              '사고 유형·업종 맞춤 필터',
              'AI 챗봇 — 사례·법령 질문',
              'JSA · TBM · 작업허가서 · 재해조사표 생성',
              '4컷 만화·체크리스트 PDF 내보내기',
              '팀 협업 워크스페이스',
            ].map(t => (
              <div key={t} className="flex items-center gap-2 text-sm text-zinc-200 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5">
                <FiCheckCircle className="text-emerald-400 flex-none" size={16} /> {t}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/pricing">
              <Button variant="primary" size="lg">
                <span className="inline-flex items-center gap-2">요금제 보러 가기 <FiArrowRight /></span>
              </Button>
            </Link>
            <Link to="/signup">
              <Button variant="ghost" size="lg">
                <span className="inline-flex items-center gap-2">무료로 시작하기</span>
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}

/** 공개 — 가장 최근 발행분 뉴스레터 (인증 X). DayBriefCard 그대로 재사용. */
function PublicLatestBrief() {
  const [bundle, setBundle] = useState<DayBriefBundle | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetch('/api/daily-brief/public/latest')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data: DayBriefBundle) => {
        if (cancelled) return
        setBundle(data)
        setLoading(false)
      })
      .catch(e => {
        if (cancelled) return
        setError(String(e?.message ?? e))
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-zinc-900/40 p-12 text-center">
        <div className="w-10 h-10 mx-auto mb-4 rounded-full border-2 border-amber-400/30 border-t-amber-400 animate-spin" />
        <p className="text-sm text-zinc-400">오늘의 뉴스레터를 불러오는 중…</p>
      </div>
    )
  }
  if (error || !bundle) {
    return (
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.05] p-8 text-center">
        <p className="text-sm text-amber-300 mb-2">아직 오늘 뉴스레터가 준비되지 않았습니다.</p>
        <p className="text-xs text-zinc-400">매일 새벽 06:40에 자동 발행 — 잠시 후 다시 확인해 주세요.</p>
      </div>
    )
  }
  // 라이트/다크 무관하게 잘 보이도록 카드 자체는 surface 토큰을 사용 (DayBriefCard 내부 클래스)
  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-900/30 backdrop-blur p-3 sm:p-5">
      <DayBriefCard bundle={bundle} defaultOpen hideHeader />
    </div>
  )
}
